import * as React from "react";
import * as _ from "underscore";
import { connect } from "react-redux";
import { isProduction, getConfig } from "config";

import { IMapStateToProps, IMapDispatchToProps } from "underwriter_dashboard/shared/types";
import HurdleTabGroup from "underwriter_dashboard/shared/HurdleTabGroup";
import { shouldPromptToBeginUnderwriting } from "underwriter_dashboard/shared/StartUnderwritingPrompt/helpers";
import StartUnderwritingPrompt from "underwriter_dashboard/shared/StartUnderwritingPrompt";
import {
  mapStateToProps as parentMapStateToProps,
  mapDispatchToProps,
} from "underwriter_dashboard/selectors";
import { AppShape, HurdleShape, HLHLContactRole } from "@eave/shared/types/api";
import {
  clearLoanAppContextForMaskingLogs,
  setLoanAppContextForMaskingLogs,
} from "../../utils/sentry";

import { getEditorForHurdle, getHurdleTabGroup } from "./editors";

require("assets/stylesheets/components/underwriter_dashboard/app_editor.scss");

const Pusher = require("pusher-js");
const DocumentTitle = require("react-document-title");

const getHurdleFromApp = (app: AppShape, hurdleName: string) =>
  _.findWhere(app.hurdles, { name: hurdleName });

interface HurdleEditorProps extends IMapStateToProps, IMapDispatchToProps {
  tabGroupName?: string;
  tabLink?: string;
  hlhlContactRole: HLHLContactRole;
}

export interface HurdleProps {
  app: AppShape;
  hurdle: HurdleShape;
  onHide: Function;
  hideTitle?: boolean; // set if hurdle in a group where tab has display name
  createNewDisclosure?: Function;
}

class HurdleEditor extends React.Component<HurdleEditorProps, any> {
  pusher: any;

  constructor(props: HurdleEditorProps) {
    super(props);

    this.clearEdit = this.clearEdit.bind(this);

    this.pusher = new Pusher(getConfig("pusher.key"), {
      encrypted: true,
      cluster: "mt1",
    });
  }

  componentDidMount() {
    const { appId, app, loadApp, editingHurdle, setCurrentHurdleName } = this.props;

    this.actionsForNewApp(appId);

    if (app) {
      // loaded by clicking through from UW dashboard
      setLoanAppContextForMaskingLogs(app);

      const editingHurdleExists = !!getHurdleFromApp(app, editingHurdle);
      if (editingHurdleExists) {
        setCurrentHurdleName(editingHurdle);
      }
    } else {
      // loaded directly from URL; need to grab app info then refresh
      loadApp(appId);
    }
  }

  getHurdleEditor(hurdle: HurdleShape) {
    const { app, createNewDisclosure } = this.props;

    const editor = getEditorForHurdle(hurdle.name);
    const sharedProps: HurdleProps = {
      hurdle,
      app,
      createNewDisclosure,
      onHide: this.clearEdit,
    };

    return React.createElement(editor, sharedProps, null);
  }

  clearEdit() {
    const { app, navigate } = this.props;

    navigate(`/underwriter/app/${app.id}`);
  }

  actionsForNewApp(appId) {
    const { loadMessages, getUnreadMessageCount } = this.props;

    if (appId) {
      loadMessages(appId);
      getUnreadMessageCount(appId);
      this.setupPusher(appId);
    }
  }

  setupPusher(applicationId) {
    const { commentReceived, messageReceived } = this.props;

    // prefix helps namespace prod in isolation to avoid crossed messages
    const prodPrefix = isProduction() ? "eave_" : "";
    const channelName = `${prodPrefix}application_${applicationId}`;
    if (!this.pusher.channels.channels.hasOwnProperty(channelName)) {
      const channel = this.pusher.subscribe(channelName);

      channel.bind("message", message => {
        if (message.thread_id) {
          commentReceived(message);
        } else {
          messageReceived(applicationId, message);
        }
      });
    }
  }

  componentDidUpdate() {
    const { app, editingHurdle, setCurrentHurdleName } = this.props;

    if (app) {
      setLoanAppContextForMaskingLogs(app);

      const editingHurdleExists = !!getHurdleFromApp(app, editingHurdle);
      if (editingHurdleExists) {
        setCurrentHurdleName(editingHurdle);
      }
    }
  }

  componentWillUnmount() {
    const { clearCurrentHurdleName } = this.props;

    clearCurrentHurdleName();
    clearLoanAppContextForMaskingLogs();
  }

  render() {
    const {
      loading,
      app,
      loadApp,
      tabGroupName,
      tabLink,
      editingHurdle,
      hlhlContactRole,
    } = this.props;

    if (
      loading &&
      !(
        editingHurdle === "DocumentsHurdle" ||
        editingHurdle === "NewDocumentsHurdle" ||
        editingHurdle === "TransactionTermsHurdle"
      )
    ) {
      return <div>Loading…</div>;
    }

    if (!app) {
      return <div>Loan app not found</div>;
    }

    if (shouldPromptToBeginUnderwriting(app, hlhlContactRole)) {
      return <StartUnderwritingPrompt app={app} loadApp={loadApp} />;
    }

    // special case for showing groups of hurdles on one screen in a tabbed layout
    const groupName = tabGroupName || editingHurdle; // e.g., "income/summary" vs. "income"
    const tabs = getHurdleTabGroup(groupName);
    if (tabs) {
      return (
        <HurdleTabGroup
          app={app}
          onHide={this.clearEdit}
          groupName={groupName}
          tabs={tabs}
          link={tabLink}
        />
      );
    }

    let hurdle = getHurdleFromApp(app, editingHurdle);

    if (editingHurdle && !hurdle) {
      hurdle = { name: editingHurdle, status: "", description: "" };
    }

    let title = `#${app.id}`;
    if (editingHurdle) {
      title = `${title}: ${editingHurdle}`;
    }

    return <DocumentTitle title={title}>{this.getHurdleEditor(hurdle)}</DocumentTitle>;
  }
}

const mapStateToProps = (state, ownProps) => {
  const { tabGroupName, tabLink } = ownProps.match.params;

  return {
    ...parentMapStateToProps(state, ownProps),
    tabGroupName,
    tabLink,
    hlhlContactRole: state.hlhlContactsRole.hlhl_contact_role,
  };
};

const ConnectedHurdleEditor = connect<IMapStateToProps, IMapDispatchToProps>(
  mapStateToProps,
  mapDispatchToProps,
)(HurdleEditor);

export default ConnectedHurdleEditor;
